/*
 *   File : index.js
 *   Author URI : https://evoqueinnovativelab.com
 *   Description : Index file for Manage AMS container;
 *   Integrations : null
 *   Version : v1.0
 *   Created : 06-05-2026
 */

/* import packages */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "bootstrap";
import $ from "jquery";

/* import components */
import { ManageAMSForm, AddAMSForm, DeleteAMS, SuccessConfirmationModal } from "../../components/modal";
import { EmptyNote, Toast } from "../../components/note";
import { Loader } from "../../components/loader";

/* import api */
import APIGet from "../../services/getApi";
import APIPost from "../../services/postApi";
import devconfig from "../../config/config";

/* import styles */
import styles from "../../styles/components/manageAMS.module.scss";

/* import assets */
import AddBlue from "../../assets/images/dataFormating/add-blue.svg";

const PER_PAGE = 10;
const TAB_CORE = 1;
const TAB_CUSTOM = 2;
const BASKET_VIEW_MODAL_ID = "basketAllocationsView";

/* normalize for fuzzy matching: list row keys come back like
 * "high_growth\nactive" while /ams/filter returns "High Growth Active".
 * Strip whitespace + non-alphanumerics + lowercase to match either form. */
const _normalize = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const _parsePercent = (raw) => {
    if (raw === null || raw === undefined) return 0;
    const num = parseFloat(String(raw).replace(/[^0-9.]/g, ""));
    return Number.isFinite(num) ? num : 0;
};

/* The list row uses dynamic per-goal keys (and includes id, name, is_active,
 * home_page_visibility). For the edit modal we need a clean
 * `{ id, name, allocations: [{goal_id, allocation}] }` shape, so map each
 * goal from /ams/filter back onto its value in the row. */
const _buildEditPayload = (row, goals) => {
    if (!row) return null;

    /* Custom AMS list returns allocations on the row directly. */
    if (Array.isArray(row.allocations)) {
        return {
            id: row.id,
            name: row.name,
            home_page_visibility: Boolean(row.home_page_visibility),
            allocations: row.allocations.map((a) => ({
                goal_id: a.goal_id,
                allocation: _parsePercent(a.allocation),
            })),
            ams_alias: row.ams_alias,
            min_sip: row.min_sip_amount,
            min_lumpsum: row.min_lumpsum_amount,
            min_invest_more: row.min_invest_more ?? row.min_invest_more_amount,
        };
    }

    const allocations = goals.map((g) => {
        const goalKey = _normalize(g.name);
        const matchKey = Object.keys(row).find((k) => _normalize(k) === goalKey);
        return {
            goal_id: g.id,
            allocation: _parsePercent(matchKey ? row[matchKey] : 0),
        };
    });
    return {
        id: row.id,
        name: row.name,
        home_page_visibility: Boolean(row.home_page_visibility),
        allocations,
        ams_alias: row.ams_alias,
        /* List API returns the saved (admin-configured) minimums as
         * min_sip_amount / min_lumpsum_amount. Map them onto the modal's
         * min_sip / min_lumpsum props so the inputs show the API value. */
        min_sip: row.min_sip_amount,
        min_lumpsum: row.min_lumpsum_amount,
        min_invest_more: row.min_invest_more ?? row.min_invest_more_amount,
    };
};

const _formatAllocationEntry = (entry) =>
    `${String(entry.goal_name ?? "").replace(/\s+/g, " ").trim()} (${_parsePercent(entry.allocation)}%)`;

const _getRowAllocations = (row) =>
    Array.isArray(row.allocations) ? row.allocations : [];

const ManageAMS = () => {

    const [activeTab, setActiveTab] = useState(TAB_CORE);
    const [pageNum, setPageNum] = useState(1);
    const [customPageNum, setCustomPageNum] = useState(1);
    const [sortAsc, setSortAsc] = useState(true);
    const [customSortAsc, setCustomSortAsc] = useState(true);
    const [data, setData] = useState([]);
    const [customData, setCustomData] = useState([]);
    const [goals, setGoals] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [customTotalRecords, setCustomTotalRecords] = useState(0);
    const [loader, setLoader] = useState(true);
    const [customLoader, setCustomLoader] = useState(false);
    const [error, setError] = useState(null);

    const [editTarget, setEditTarget] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);
    const [showAddAMSModal, setShowAddAMSModal] = useState(false);
    const [editCustomTarget, setEditCustomTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [viewMoreTarget, setViewMoreTarget] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [pendingSuccess, setPendingSuccess] = useState(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    useEffect(() => {
        if (error !== null) {
            const t = setTimeout(() => setError(null), 3000);
            return () => clearTimeout(t);
        }
    }, [error]);

    /* fetch goal definitions once — drives both the form rows and the
     * table columns. */
    const _getGoalFilter = useCallback(() => {
        const url = devconfig.admin + "/ams/filter";
        APIGet(url).then((response) => {
            if (response?.status_code === 200 || response?.status === true) {
                const list = Array.isArray(response.data) ? response.data : [];
                setGoals(list);
            } else {
                setGoals([]);
                setError(response?.message || "Failed to load AMS goals");
            }
        }).catch(() => {
            setGoals([]);
            setError("Failed to load AMS goals");
        });
    }, []);

    /* AMS list — payload shape (page/limit/query/is_active) follows the
     * pattern used by other list endpoints in this app. Adjust here if the
     * /ams/list contract is different. */
    const _getAMSList = useCallback((showLoader = true) => {
        if (showLoader) setLoader(true);

        const url = devconfig.admin + "/ams/list";
        const payload = JSON.stringify({
            page_num: pageNum,
            page_size: PER_PAGE,
        });

        APIPost(url, payload).then((response) => {
            if (response?.status_code === 200 || response?.status === true) {
                /* tolerate either a flat array or a paginated envelope under data */
                const raw = response.data;
                const rows = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.data) ? raw.data : [];
                const total = response.total_count
                    ?? raw?.total_count
                    ?? raw?.total_records
                    ?? rows.length;
                setData(rows);
                setTotalRecords(total);
            } else {
                setData([]);
                setTotalRecords(0);
                setError(response?.message || "Failed to load AMS list");
            }
        }).catch(() => {
            setData([]);
            setTotalRecords(0);
            setError("Failed to load AMS list");
        }).finally(() => {
            if (showLoader) setLoader(false);
        });
    }, [pageNum]);

    const _getCustomAMSList = useCallback((showLoader = true) => {
        if (showLoader) setCustomLoader(true);

        const url = devconfig.admin + "/ams/custom-list";
        const payload = JSON.stringify({
            page_num: customPageNum,
            page_size: PER_PAGE,
        });

        APIPost(url, payload).then((response) => {
            if (response?.status_code === 200 || response?.status === true) {
                const raw = response.data;
                const rows = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.data) ? raw.data : [];
                const total = response.total_count
                    ?? raw?.total_count
                    ?? raw?.total_records
                    ?? rows.length;
                setCustomData(rows);
                setCustomTotalRecords(total);
            } else {
                setCustomData([]);
                setCustomTotalRecords(0);
                setError(response?.message || "Failed to load custom AMS list");
            }
        }).catch(() => {
            setCustomData([]);
            setCustomTotalRecords(0);
            setError("Failed to load custom AMS list");
        }).finally(() => {
            if (showLoader) setCustomLoader(false);
        });
    }, [customPageNum]);

    const _refreshLists = useCallback(() => {
        if (activeTab === TAB_CORE) {
            _getAMSList(true);
        } else {
            _getCustomAMSList(true);
        }
    }, [activeTab, _getAMSList, _getCustomAMSList]);

    useEffect(() => {
        _getGoalFilter();
    }, [_getGoalFilter]);

    useEffect(() => {
        _getAMSList();
    }, [_getAMSList]);

    useEffect(() => {
        if (activeTab === TAB_CUSTOM) {
            _getCustomAMSList();
        }
    }, [_getCustomAMSList, activeTab]);

    /* open modals */
    useEffect(() => {
        if (!showFormModal) return;
        const el = document.getElementById("manageAMSForm");
        if (!el) return;
        const modal = new Modal(el, {});
        modal.show();
    }, [showFormModal]);

    useEffect(() => {
        if (!showAddAMSModal) return;
        const el = document.getElementById("addAMSForm");
        if (!el) return;
        const modal = new Modal(el, {});
        modal.show();
    }, [showAddAMSModal]);

    useEffect(() => {
        if (deleteTarget === null) return;
        const el = document.getElementById("deleteAMS");
        if (!el) return;
        const modal = new Modal(el, {});
        modal.show();
    }, [deleteTarget]);

    useEffect(() => {
        if (viewMoreTarget === null) return;
        const el = document.getElementById(BASKET_VIEW_MODAL_ID);
        if (!el) return;
        const modal = new Modal(el, {});
        modal.show();
    }, [viewMoreTarget]);

    useEffect(() => {
        if (viewMoreTarget === null) return;
        const modalEl = document.getElementById(BASKET_VIEW_MODAL_ID);
        if (!modalEl) return;
        const handler = () => {
            $(".modal-backdrop").remove();
            $("body").removeAttr("style");
            setViewMoreTarget(null);
        };
        modalEl.addEventListener("hidden.bs.modal", handler);
        return () => modalEl.removeEventListener("hidden.bs.modal", handler);
    }, [viewMoreTarget]);

    useEffect(() => {
        if (successMessage === null) return;
        const el = document.getElementById("confirmSuccess");
        if (!el) return;
        const modal = new Modal(el, {});
        modal.show();
    }, [successMessage]);

    const _openCreate = () => {
        if (goals.length === 0) {
            setError("Goal list is still loading");
            return;
        }
        setEditTarget(null);
        setShowFormModal(true);
    };

    /* New custom AMS — no baskets pre-loaded; admin searches & selects. */
    const _openCreateCustom = () => {
        setEditCustomTarget(null);
        setShowAddAMSModal(true);
    };

    const _openEdit = (row) => {
        if (activeTab === TAB_CUSTOM) {
            setEditCustomTarget(_buildEditPayload(row, goals));
            setShowAddAMSModal(true);
            return;
        }
        if (goals.length === 0) {
            setError("Goal list is still loading");
            return;
        }
        setEditTarget(_buildEditPayload(row, goals));
        setShowFormModal(true);
    };

    const _openDelete = (row) => {
        setDeleteTarget(row);
    };

    const _openViewMore = (row) => {
        setViewMoreTarget(row);
    };

    const _closeViewMore = () => {
        setViewMoreTarget(null);
    };

    const _dismissViewMore = () => {
        $("#basket-alloc-view-dismiss").trigger("click");
    };

    const _openEditFromViewMore = () => {
        const row = viewMoreTarget;
        _dismissViewMore();
        setTimeout(() => _openEdit(row), 250);
    };

    const _openDeleteFromViewMore = () => {
        const row = viewMoreTarget;
        _dismissViewMore();
        setTimeout(() => _openDelete(row), 250);
    };

    /* Create / Update — single upsert endpoint. Modal hands us:
     *   { id, name, allocations: [{ goal_id, allocation }] }
     * id is null for create; on edit we keep home_page_visibility from
     * editTarget so we don't accidentally flip it. */
    const _handleFormSubmit = (payload, isEdit, dismissId = "ams-form-dismiss", isCoreAMS = true) => {
        const url = devconfig.admin + "/ams/upsert";

        const body = JSON.stringify({
            id: isEdit ? payload.id : null,
            name: payload.name,
            home_page_visibility: isEdit
                ? Boolean(editTarget?.home_page_visibility ?? editCustomTarget?.home_page_visibility)
                : false,
            ams_alias: payload.ams_alias,
            allocations: payload.allocations,
            min_sip: payload.min_sip,
            min_lumpsum: payload.min_lumpsum,
            min_invest_more: payload.min_invest_more,
            is_core_ams: isCoreAMS,
        });

        return APIPost(url, body).then((response) => {
            if (response?.status_code === 200 || response?.status === true) {
                if (isCoreAMS && !isEdit) {
                    setActiveTab(TAB_CORE);
                    _getAMSList(true);
                } else if (!isCoreAMS && !isEdit) {
                    setActiveTab(TAB_CUSTOM);
                    _getCustomAMSList(true);
                } else {
                    _refreshLists();
                }
                setPendingSuccess(isEdit ? "AMS Updated Successfully" : "AMS Created Successfully");
                $("#" + dismissId).trigger("click");
            } else {
                const msg = response?.message || "Failed to save AMS";
                return Promise.reject(msg);
            }
        }).catch((errMsg) => {
            const msg = typeof errMsg === "string" ? errMsg : "Failed to save AMS";
            return Promise.reject(msg);
        });
    };

    const _handleFormClose = () => {
        setShowFormModal(false);
        setEditTarget(null);
        if (pendingSuccess !== null) {
            const msg = pendingSuccess;
            setPendingSuccess(null);
            setTimeout(() => setSuccessMessage(msg), 250);
        }
    };

    const _handleAddAMSClose = () => {
        setShowAddAMSModal(false);
        setEditCustomTarget(null);
        if (pendingSuccess !== null) {
            const msg = pendingSuccess;
            setPendingSuccess(null);
            setTimeout(() => setSuccessMessage(msg), 250);
        }
    };

    const _handleDelete = (row) => {
        const url = devconfig.admin + "/ams/delete";
        const body = JSON.stringify({ id: row.id });

        return APIPost(url, body).then((response) => {
            if (response?.status_code === 200 || response?.status === true) {
                _refreshLists();
                setPendingSuccess("AMS Deleted Successfully");
                $("#ams-delete-dismiss").trigger("click");
            } else {
                setError(response?.message || "Failed to delete AMS");
            }
        }).catch(() => {
            setError("Failed to delete AMS");
        });
    };

    const _handleDeleteClose = () => {
        setDeleteTarget(null);
        if (pendingSuccess !== null) {
            const msg = pendingSuccess;
            setPendingSuccess(null);
            setTimeout(() => setSuccessMessage(msg), 250);
        }
    };

    const _handleCloseSuccess = () => {
        setSuccessMessage(null);
    };

    /* Sort by AMS name client-side. If the list endpoint exposes server-side
     * sort, swap this for a request param. */
    const sortedData = useMemo(() => {
        const copy = [...data];
        copy.sort((a, b) => sortAsc
            ? (a.name || "").localeCompare(b.name || "")
            : (b.name || "").localeCompare(a.name || ""));
        return copy;
    }, [data, sortAsc]);

    const sortedCustomData = useMemo(() => {
        const copy = [...customData];
        copy.sort((a, b) => customSortAsc
            ? (a.name || "").localeCompare(b.name || "")
            : (b.name || "").localeCompare(a.name || ""));
        return copy;
    }, [customData, customSortAsc]);

    const isCoreTab = activeTab === TAB_CORE;
    const activeTotalRecords = isCoreTab ? totalRecords : customTotalRecords;
    const activeSortedData = isCoreTab ? sortedData : sortedCustomData;
    const activePageNum = isCoreTab ? pageNum : customPageNum;
    const activeLoader = isCoreTab ? loader : customLoader;

    const totalPages = Math.max(1, Math.ceil(activeTotalRecords / PER_PAGE));

    useEffect(() => {
        if (pageNum > Math.max(1, Math.ceil(totalRecords / PER_PAGE))) {
            setPageNum(Math.max(1, Math.ceil(totalRecords / PER_PAGE)));
        }
    }, [pageNum, totalRecords]);

    useEffect(() => {
        if (customPageNum > Math.max(1, Math.ceil(customTotalRecords / PER_PAGE))) {
            setCustomPageNum(Math.max(1, Math.ceil(customTotalRecords / PER_PAGE)));
        }
    }, [customPageNum, customTotalRecords]);

    const safePage = Math.min(activePageNum, totalPages);
    const startIdx = (safePage - 1) * PER_PAGE;
    const endIdx = Math.min(startIdx + PER_PAGE, activeTotalRecords);

    const pageData = activeTotalRecords > activeSortedData.length
        ? activeSortedData
        : activeSortedData.slice(startIdx, endIdx);

    const _goToPage = (target) => {
        const safe = Math.min(Math.max(1, target), totalPages);
        if (isCoreTab) setPageNum(safe);
        else setCustomPageNum(safe);
    };

    /* Look up the per-goal value on a list row. Tries the canonical key
     * first (goal name normalized), falls back to scanning row keys. */
    const _readAllocation = (row, goal) => {
        const goalKey = _normalize(goal.name);
        const matchKey = Object.keys(row).find((k) => _normalize(k) === goalKey);
        return matchKey ? row[matchKey] : "0%";
    };

    const _renderBasketAllocationPreview = (row) => {
        const allocations = _getRowAllocations(row);
        if (allocations.length === 0) return "-";
        return allocations.map(_formatAllocationEntry).join(", ");
    };

    const _getViewMoreCount = (row) => {
        const count = _getRowAllocations(row).length;
        return count > 2 ? count - 2 : 0;
    };

    return (
        <>
            <div className="row">
                <div className="col-lg-12">
                    <div className={styles.e_ams_card}>
                        <div className={styles.e_tab_row}>
                            <div className={styles.e_tabs}>
                                <button type="button"
                                    className={activeTab === TAB_CORE ? styles.e_active_tab : styles.e_tab}
                                    onClick={() => setActiveTab(TAB_CORE)}>
                                    Core AMS
                                </button>
                                <button type="button"
                                    className={activeTab === TAB_CUSTOM ? styles.e_active_tab : styles.e_tab}
                                    onClick={() => setActiveTab(TAB_CUSTOM)}>
                                    Custom AMS
                                </button>
                            </div>
                            <div className={styles.e_actions}>
                                <button className={styles.e_add_btn} type="button" onClick={_openCreate}>
                                    <img src={AddBlue} alt="" />
                                    <span>Add Core AMS</span>
                                </button>
                                <button className={styles.e_add_btn} type="button" onClick={_openCreateCustom}>
                                    <img src={AddBlue} alt="" />
                                    <span>Add Custom AMS</span>
                                </button>
                            </div>
                        </div>

                        <div className={styles.e_table_wrap}>
                            {activeLoader ? (
                                <Loader />
                            ) : pageData.length === 0 ? (
                                <EmptyNote note="There are no AMS to show" />
                            ) : isCoreTab ? (
                                <table className={styles.e_table}>
                                    <thead>
                                        <tr>
                                            <th onClick={() => setSortAsc((prev) => !prev)}>
                                                AMS Name
                                                <span className={styles.e_sort_icon}>&uarr;&darr;</span>
                                            </th>
                                            <th>AMS Alias</th>
                                            {goals.map((goal) => (
                                                <th key={goal.id} style={{ whiteSpace: "pre-line" }}>
                                                    {goal.name}
                                                </th>
                                            ))}

                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageData.map((row) => (
                                            <tr key={row.id}>
                                                <td>{row.name}</td>
                                                <td>{row.ams_alias || "-"}</td>
                                                {goals.map((goal) => (
                                                    <td key={goal.id}>{_readAllocation(row, goal)}</td>
                                                ))}
                                                <td>
                                                    <div className={styles.e_actions}>
                                                        <button type="button"
                                                            className={styles.e_edit_btn}
                                                            onClick={() => _openEdit(row)}>Edit</button>
                                                        <button type="button"
                                                            className={styles.e_delete_btn}
                                                            onClick={() => _openDelete(row)}>Delete</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <table className={styles.e_table}>
                                    <thead>
                                        <tr>
                                            <th onClick={() => setCustomSortAsc((prev) => !prev)}>
                                                AMS Name
                                                <span className={styles.e_sort_icon}>&uarr;&darr;</span>
                                            </th>
                                            <th>AMS Alias</th>
                                            <th>Basket Allocation</th>
                                            <th className={styles.e_actions_cell}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageData.map((row) => {
                                            return (
                                            <tr key={row.id}>
                                                <td>{row.name}</td>
                                                <td>{row.ams_alias || "-"}</td>
                                                <td className={styles.e_basket_alloc_cell}>
                                                    {_renderBasketAllocationPreview(row)}
                                                </td>
                                                <td className={styles.e_actions_cell}>
                                                    <div className={styles.e_actions_wrap}>
                                                        {/* allocations now wrap onto a 2nd line in the cell above instead of a "+N view more" trigger
                                                        {viewMoreCount > 0 && (
                                                            <button type="button"
                                                                className={styles.e_view_more}
                                                                onClick={() => _openViewMore(row)}>
                                                                +{viewMoreCount} View more
                                                            </button>
                                                        )} */}
                                                        <button type="button"
                                                            className={styles.e_edit_btn}
                                                            onClick={() => _openEdit(row)}>Edit</button>
                                                        <button type="button"
                                                            className={styles.e_delete_btn}
                                                            onClick={() => _openDelete(row)}>Delete</button>
                                                    </div>
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className={styles.e_pagination_wrap}>
                            <span className={styles.e_pagination_info}>
                                Showing {activeTotalRecords === 0 ? 0 : startIdx + 1}-{endIdx} of {activeTotalRecords} entries
                            </span>
                            <div className={styles.e_pagination_buttons}>
                                <button type="button"
                                    className={styles.e_pagination_btn}
                                    disabled={safePage === 1}
                                    onClick={() => _goToPage(1)}
                                    aria-label="First page">
                                    <svg viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M3.5 2.5h1v11h-1v-11zM12.354 3.354L7.707 8l4.647 4.646-.707.708L6.293 8l5.354-5.354.707.708z" />
                                    </svg>
                                </button>
                                <button type="button"
                                    className={styles.e_pagination_btn}
                                    disabled={safePage === 1}
                                    onClick={() => _goToPage(safePage - 1)}
                                    aria-label="Previous page">
                                    <svg viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M10.354 3.354L5.707 8l4.647 4.646-.707.708L4.293 8l5.354-5.354.707.708z" />
                                    </svg>
                                </button>
                                <button type="button"
                                    className={styles.e_pagination_btn}
                                    disabled={safePage === totalPages}
                                    onClick={() => _goToPage(safePage + 1)}
                                    aria-label="Next page">
                                    <svg viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M5.646 3.354L10.293 8l-4.647 4.646.707.708L11.707 8 6.354 2.646l-.708.708z" />
                                    </svg>
                                </button>
                                <button type="button"
                                    className={styles.e_pagination_btn}
                                    disabled={safePage === totalPages}
                                    onClick={() => _goToPage(totalPages)}
                                    aria-label="Last page">
                                    <svg viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M11.5 2.5h1v11h-1v-11zM3.646 3.354L8.293 8l-4.647 4.646.707.708L9.707 8 4.354 2.646l-.708.708z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showFormModal && (
                <ManageAMSForm
                    data={editTarget}
                    goals={goals}
                    onSubmit={(payload, isEdit) => _handleFormSubmit(payload, isEdit, "ams-form-dismiss", true)}
                    closeModal={_handleFormClose} />
            )}

            {showAddAMSModal && (
                <AddAMSForm
                    data={editCustomTarget}
                    onSubmit={(payload, isEdit) => _handleFormSubmit(payload, isEdit, "add-ams-form-dismiss", false)}
                    closeModal={_handleAddAMSClose} />
            )}

            {deleteTarget !== null && (
                <DeleteAMS
                    data={deleteTarget}
                    onConfirm={_handleDelete}
                    closeModal={_handleDeleteClose} />
            )}

            {viewMoreTarget !== null && (
                <div className={`${styles.e_modal} modal fade`}
                    id={BASKET_VIEW_MODAL_ID}
                    tabIndex="-1"
                    data-bs-backdrop="static"
                    aria-labelledby="basketAllocationsView"
                    aria-hidden="true">
                    <div className={`${styles.e_modal_dialog} modal-dialog modal-dialog-centered`}>
                        <div className="modal-content">
                            <div className="modal-header border-0 pb-0">
                                <h6 className="modal-title">Basket Allocations</h6>
                                <button type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                    onClick={_closeViewMore} />
                                <button type="button"
                                    className="d-none"
                                    id="basket-alloc-view-dismiss"
                                    data-bs-dismiss="modal"
                                    aria-label="Close" />
                            </div>
                            <div className="modal-body pt-2">
                                <table className={styles.e_view_modal_table}>
                                    <thead>
                                        <tr>
                                            <th>Basket Name</th>
                                            <th>Allocations (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(viewMoreTarget.allocations ?? []).map((entry) => (
                                            <tr key={entry.goal_id}>
                                                <td>{String(entry.goal_name ?? "").replace(/\s+/g, " ").trim()}</td>
                                                <td>{_parsePercent(entry.allocation)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="d-flex justify-content-end gap-3">
                                    <button type="button"
                                        className={styles.e_edit_btn}
                                        onClick={_openEditFromViewMore}>
                                        Edit
                                    </button>
                                    <button type="button"
                                        className={styles.e_delete_btn}
                                        onClick={_openDeleteFromViewMore}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {successMessage !== null && (
                <SuccessConfirmationModal
                    type={2}
                    message={successMessage}
                    closeModal={_handleCloseSuccess} />
            )}

            {error !== null ? <Toast data={error} type="error" id="ams-list-error" /> : null}
        </>
    );
};

export default ManageAMS;
